import { ReactElement, useContext, useEffect, useState } from 'react';
import { useQuery } from 'utils/hooks/useQuery';
import { useDispatch } from 'react-redux';
import { clearMessages } from 'store/ancillary/actions';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import { declineEpicCareLinkUserCreation, checkIfEpicCareLinkSiteIsProcessed, checkIfEpicCareLinkIsDeclined } from '../_api';
import { PAGE_ICONS } from '../_const';
import { UserContext } from 'context/User/UserContext';
import DomPurify from 'dompurify';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import PageWithBreadcrumbs from '../components/layouts/NEPageWithBreadcrumbs';
import ViewLayout from '../components/layouts/ViewLayout';
import Typography from '@mui/material/Typography';

/**
 * EpicCare Link User - new non-employee creation workflow
 * @returns {ReactElement} New EpicCare Link User Non-Employee Form with help panel, breadcrumb navigation
 */
const EpicCareLinkDecline = () => {
	const fMessageId = useQuery().get('messageId');
	const sponsorEmail = useQuery().get('sponsorEmail');

	const [comments, setComments] = useState('');
	const [pendingRequest, setPendingRequest] = useState(false);

	const defaultMessage = 'There was an error retrieving information for this site request.';
	const userDeclinedMessage = 'Decline successful. Thank you for your submission.';
	const isProcessedMessage = 'This site request has already been processed or is not eligible for processing.';
	const isDeclinedMessage = 'This site request has already been declined.';
	const [disabledState, setDisabledState] = useState({ isTouched: true, message: defaultMessage })

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);

	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const dispatch = useDispatch();

	useEffect(() => {
		// First check if already declined
		checkIfEpicCareLinkIsDeclined(fMessageId, sponsorEmail)
			.then(res => {
				if (res && res.status === 200) {
					if (res.data.payload)
						setDisabledState({ isTouched: res.data.payload, message: isDeclinedMessage });
					else {
						// If not already declined, ensure site hasn't been processed
						checkIfEpicCareLinkSiteIsProcessed(fMessageId)
							.then(res => {
								if (res && res.status === 200)
									setDisabledState({ isTouched: res.data.payload, message: isProcessedMessage });
							})
							.catch(() => {
								setDisabledState(prevError => ({ ...prevError, isTouched: true }));
								raiseErrorBanner('Error retrieving site request information.', dispatch);
							});
					}
				}
			})
			.catch(() => {
				setDisabledState(prevError => ({ ...prevError, isTouched: true }));
				raiseErrorBanner('Error retrieving site request information.', dispatch);
			});
	}, [dispatch, fMessageId, sponsorEmail]);

	const onSubmitDecline = async () => {
		dispatch(clearMessages());
		if (fMessageId && sponsorEmail) {
			setPendingRequest(true);
			await declineEpicCareLinkUserCreation(fMessageId, sponsorEmail, comments, user)
				.then(res => {
					if (res.status === 200) {
						setDisabledState({ isTouched: true, message: userDeclinedMessage });
					}
					setPendingRequest(false);
				})
				.catch(() => {
					raiseErrorBanner('Error declining user.', dispatch);
					setPendingRequest(false);
				});
		}
	};

	return (
		<PageWithBreadcrumbs
			currentPageLabel={'Decline EpicCare Link User'}
			CurrentPageIcon={PAGE_ICONS.IDENTITY}
		>
			<ViewLayout
				title={'Decline EpicCare Link User'}
			>
				{disabledState.isTouched ?
					<Typography>
						{disabledState.message}
					</Typography>
					:
					<Stack spacing={2} justifyContent='center'>
						<TextField
							autoFocus
							disabled={pendingRequest}
							margin='dense'
							label='Reason for Decline'
							fullWidth
							variant='outlined'
							multiline
							rows={4}
							value={comments}
							onChange={e => setComments(DomPurify.sanitize(e.target.value))}
						/>
						<Stack spacing={2} direction='row' alignItems='center'>
							<Button
								disabled={pendingRequest}
								sx={{ width: '15%', padding: '15px' }}
								variant='contained'
								onClick={() => {
									onSubmitDecline();
								}}
							>
								Decline
							</Button>
							{pendingRequest && <CircularProgress size={30} />}
						</Stack>
					</Stack>
				}
			</ViewLayout>
		</PageWithBreadcrumbs>
	);
};

export default EpicCareLinkDecline;
